<template>
  <div class="w-full relative">
    <input
      class="w-full h-18 u-body-md px-5 py-3 border-1 border-primary rounded-lg transition-all outline-none hover:shadow-why-benefits-card text-black disabled:bg-white disabled:border-grey-300 disabled:text-grey-400 disabled:shadow-none"
      :placeholder="placeholder"
      v-model="searchInputValue"
      @input="inputHandler"
      @keypress="inputKeypressHandler"
      :disabled="!ready || searching"
    />
    <div
      ref="searchButtonWrapper"
      class="absolute top-1/2 -translate-y-1/2 right-3"
    >
      <button
        type="button"
        class="inline-flex relative flex-nowrap items-center group focus:outline-none bg-primary-400 hover:bg-white text-grey-900 rounded-lg h-12 px-2 xs:px-4 py-3 border-2 border-solid border-primary-400 transition-colors duration-200 ease-in-out disabled:bg-grey-200 disabled:text-grey-600 disabled:border-0 disabled:cursor-not-allowed"
        :disabled="!ready || searching"
        @click="searchHandler"
      >
        <span
          class="absolute -inset-1.5 opacity-0 group-focus:opacity-100 border-2 border-grey-900 border-solid rounded-lg transition-opacity duration-200 ease-in-out pointer-events-none"
        ></span>
        <span
          class="u-body-md font-bold border-b-2 border-solid border-transparent group-hover:border-current transition-colors duration-200 ease-in-out group-disabled:border-transparent"
        >
          Search
        </span>
      </button>
    </div>
    <p
      class="absolute -bottom-6 left-0 ml-5 text-xs text-error"
      v-if="error !== ''"
      v-html="error"
    ></p>
  </div>
</template>

<script>
import { ApolloClient, InMemoryCache, gql } from '@apollo/client';

import { COMMUNITY_TYPE, HOMES_TYPE } from '../configs/type';
import {
  GetLocationDataByPostcode,
  GetLocationDataBySuburb,
  GetNearestCommunities,
} from './mixins';

export default {
  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return [COMMUNITY_TYPE, HOMES_TYPE].includes(value);
      },
    },
    communities: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
    },
    query: {
      type: String,
      required: false,
    },
    nearbyRedirectUrl: {
      type: String,
      required: true,
    },
  },
  mixins: [
    GetLocationDataByPostcode,
    GetLocationDataBySuburb,
    GetNearestCommunities,
  ],
  data() {
    return {
      ready: false,
      searching: false,
      googleMapsClient: null,
      amountOfTopResults: 3,
      searchInputValue: '',
      error: '',
    };
  },
  async mounted() {
    if (this.query) this.searchInputValue = this.query;
    this.ready = true;
  },
  methods: {
    inputHandler() {
      this.error = '';
    },
    inputKeypressHandler(event) {
      if (
        event.charCode === 13 ||
        event.code === 'Enter' ||
        event.key === 'Enter' ||
        event.keyCode === 13
      ) {
        this.searchHandler();
      }
    },
    validateInputValue() {
      const postcodeRegex = new RegExp(/^[0-9]{4}$/);
      const suburbRegex = new RegExp(/^([A-Za-z'\s]*)$/);
      if (this.searchInputValue !== '') {
        if (postcodeRegex.test(this.searchInputValue)) {
          return 'postcode';
        } else if (suburbRegex.test(this.searchInputValue)) {
          return 'suburb';
        } else {
          this.error = 'Please enter a postcode or suburb.';
          return false;
        }
      } else {
        this.error = 'Please enter a postcode or suburb.';
        return false;
      }
    },
    searchCommunityEntries() {
      return new Promise(async (resolve, reject) => {
        try {
          const apolloClient = new ApolloClient({
            uri: '/api',
            cache: new InMemoryCache(),
          });

          const {
            data: { communities },
          } = await apolloClient.query({
            query: gql`
              query getCommunities($titleSearch: String) {
                communities: entries(
                  section: ["communitiesNew"]
                  type: "default"
                  search: $titleSearch
                ) {
                  id
                  ... on communitiesNew_default_Entry {
                    title
                  }
                }
              }
            `,
            variables: {
              titleSearch: `title:"${this.searchInputValue}"`,
            },
          });

          resolve(
            communities.length
              ? communities.map(community => parseInt(community.id))
              : []
          );
        } catch (error) {
          reject(error);
        }
      });
    },
    async searchHandler() {
      this.error = '';
      this.searching = true;

      const inputType = this.validateInputValue();

      if (typeof inputType === 'string') {
        try {
          if (inputType === 'postcode') {
            try {
              const locationData = await this.getLocationDataByPostcode(
                this.searchInputValue
              );
              const nearestCommunities = await this.getNearestCommunities(
                {
                  latitude: locationData.latitude,
                  longitude: locationData.longitude,
                  resultCount: this.amountOfTopResults,
                },
                this.communities
              );

              const nearestCommunityData = {
                suburb: locationData.suburb,
                postcode: locationData.postcode,
                communities: nearestCommunities,
              };

              const fullRedirectUrl = [
                this.nearbyRedirectUrl,
                '/results?',
                [
                  `query=${encodeURIComponent(this.searchInputValue)}`,
                  `nearestCommunityData=${JSON.stringify(
                    nearestCommunityData
                  )}`,
                ].join('&'),
              ].join('');

              window.location.assign(fullRedirectUrl);
            } catch (error) {
              if (error === 'ZERO_RESULTS') {
                this.error = 'Invalid postcode, please try again.';
              } else if (error === 'NOT_VIC') {
                this.error = 'Please enter a Victorian postcode.';
              } else {
                console.error(error);
                this.error = 'Something went wrong. Please try again.';
              }
              this.searching = false;
            }
          } else if (inputType === 'suburb') {
            try {
              const communitySearchData = await this.searchCommunityEntries();

              let nearestCommunityData = null;
              try {
                const locationData = await this.getLocationDataBySuburb(
                  this.searchInputValue
                );
                const nearestCommunities = await this.getNearestCommunities(
                  {
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                    resultCount: this.amountOfTopResults,
                  },
                  this.communities
                );

                nearestCommunityData = {
                  suburb: locationData.suburb,
                  postcode: locationData.postcode,
                  communities: nearestCommunities,
                };
              } catch (error) {
                if (!communitySearchData.length) {
                  if (error === 'ZERO_RESULTS') {
                    this.error = 'Invalid suburb, please try again.';
                  } else if (error === 'NOT_VIC') {
                    this.error = 'Please enter a Victorian suburb.';
                  } else {
                    console.error(error);
                    this.error = 'Something went wrong. Please try again.';
                  }

                  this.searching = false;
                  return;
                }
              }

              const fullRedirectUrl = [
                this.nearbyRedirectUrl,
                '/results?',
                [
                  `query=${encodeURIComponent(this.searchInputValue)}`,
                  ...(communitySearchData.length
                    ? [
                        `communitySearchData=${JSON.stringify(
                          communitySearchData
                        )}`,
                      ]
                    : []),
                  ...(nearestCommunityData !== null
                    ? [
                        `nearestCommunityData=${JSON.stringify(
                          nearestCommunityData
                        )}`,
                      ]
                    : []),
                ].join('&'),
              ].join('');

              window.location.assign(fullRedirectUrl);
            } catch (error) {
              console.error(error);
              this.searching = false;
            }
          }
        } catch (error) {
          this.error = error.message;
          this.searching = false;
        }
      } else {
        this.searching = false;
      }
    },
  },
};
</script>
